import React from 'react';
import { Typography, Checkbox, Box } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';

interface LabResult {
  id: number;
  study?: string;
  studyName?: string;
  labValue?: string;
  labTestType?: string;
  labTestName?: string;
  result?: string;
  testDate?: string;
  comment?: string;
  status?: 'synced' | 'pending';
}

interface LabResultsInCDPProps {
  labResults?: LabResult[];
  selectedItems: Set<string>;
  onCheckboxChange: (itemId: string, itemType: string) => void;
  showSyncColumn?: boolean;
  fieldConfigs?: any;
  headerText?: string;
}

const LabResultsInCDPSection: React.FC<LabResultsInCDPProps> = ({
  labResults,
  selectedItems,
  onCheckboxChange,
  showSyncColumn = true,
  headerText,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  if (!labResults || labResults.length === 0) return null;

  const columns: DynamicColumn<LabResult>[] = [
    {
      id: 'study',
      label: 'Study',
      accessor: 'study',
      render: (row) => {
        const study = row.study || row.studyName || '-';
        return <Typography sx={{ fontSize: '12px' }}>{study}</Typography>;
      },
    },
    {
      id: 'labValue',
      label: 'Lab Value',
      accessor: 'labValue',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.labValue || '-'}</Typography>,
    },
    {
      id: 'labTestType',
      label: 'Lab Test Type',
      accessor: 'labTestType',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.labTestType || '-'}</Typography>,
    },
    {
      id: 'labTestName',
      label: 'Lab Test Name',
      accessor: 'labTestName',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.labTestName || '-'}</Typography>,
    },
    {
      id: 'result',
      label: 'Result',
      accessor: 'result',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.result || '-'}</Typography>,
    },
    {
      id: 'testDate',
      label: 'Test Date',
      accessor: 'testDate',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.testDate || '-'}</Typography>,
    },
    {
      id: 'comment',
      label: 'Comment',
      accessor: 'comment',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.comment || '-'}</Typography>,
    },
    ...(showSyncColumn
      ? [
          {
            id: 'sync',
            label: 'Sync',
            accessor: 'status' as keyof LabResult,
            render: (row: LabResult) => (
              <Box sx={{ textAlign: 'center' }}>
                {row.status === 'synced' ? (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '11px', color: '#4caf50', fontWeight: 500 }}
                  >
                    Synced
                  </Typography>
                ) : (
                  <Checkbox
                    checked={selectedItems.has(`labResult-${row.id}`)}
                    onChange={() => onCheckboxChange(row.id.toString(), 'labResult')}
                    size="small"
                    sx={{
                      color: '#255E5E',
                      '&.Mui-checked': {
                        color: '#255E5E',
                      },
                    }}
                  />
                )}
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <CustomAccordion
      title={headerText || `Lab Results in CDP (${labResults.length})`}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1.5,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
          backgroundColor: '#255E5E',
          color: 'white',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
          color: 'white !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
          p: 0,
          border: '2px solid #255E5E',
          borderTop: 'none',
        },
      }}
      summarySx={{
        backgroundColor: '#255E5E',
        color: 'white',
        '& .MuiTypography-root': {
          color: 'white',
          fontSize: '15px',
          fontWeight: 400,
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
      detailsSx={{
        p: 0,
        border: '2px solid #255E5E',
        borderTop: 'none',
      }}
    >
      <DynamicTable
        columns={columns}
        data={labResults}
        variant="compact"
        noDataMessage="No lab results found"
      />
    </CustomAccordion>
  );
};

export default LabResultsInCDPSection;
